import React, { useCallback, useEffect, useState } from "react";
import {
  Stack,
  Grid,
  Typography,
  Button,
  Alert,
  Box,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { MdOpenInNew, MdDelete, MdAdd, MdDescription } from "react-icons/md";

import { Surface, SectionTitle, ConfirmDialog } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "../api";

const BUCKETS = [
  { value: "businessDocumets", label: "Business documents" },
  { value: "userDocumets", label: "Owner documents" },
  { value: "pharmacyImage", label: "Pharmacy photos" },
];

const bucketLabel = (bucket) =>
  BUCKETS.find((b) => b.value === bucket)?.label || bucket;

const isImage = (url) => /\.(png|jpe?g|webp|gif|avif)(\?|$)/i.test(url || "");

function DocumentCard({ doc, onOpen, onRemove }) {
  return (
    <Surface sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          height: 150,
          bgcolor: T.borderSoft,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
        onClick={onOpen}
      >
        {isImage(doc.url) ? (
          <Box
            component="img"
            src={doc.url}
            alt={doc.name || "Document"}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          // PDFs and the like have no thumbnail; an icon beats a broken image.
          <Stack alignItems="center" spacing={0.5}>
            <MdDescription size={34} color={T.subtle} />
            <Typography sx={{ fontSize: "0.72rem", color: T.subtle }}>
              Open document
            </Typography>
          </Stack>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.2, py: 1 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: T.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {doc.name || doc.url.split("/").pop()}
          </Typography>
          <Typography sx={{ fontSize: "0.68rem", color: T.subtle }}>
            {bucketLabel(doc.bucket)}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.3}>
          <Button size="small" onClick={onOpen} sx={{ minWidth: 0, color: T.secondary }}>
            <MdOpenInNew size={16} />
          </Button>
          <Button size="small" onClick={onRemove} sx={{ minWidth: 0, color: T.error }}>
            <MdDelete size={16} />
          </Button>
        </Stack>
      </Stack>
    </Surface>
  );
}

export default function DocumentsTab({ pharmacyId, notify }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ bucket: BUCKETS[0].value, url: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState("");

  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.getDocuments(pharmacyId);
      setDocuments(res.documents || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load documents.");
    } finally {
      setLoading(false);
    }
  }, [pharmacyId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    setSaving(true);
    setAddError("");
    try {
      await api.addDocument(pharmacyId, form);
      notify?.("Document added.");
      setAdding(false);
      setForm({ bucket: BUCKETS[0].value, url: "", name: "" });
      await load();
    } catch (err) {
      setAddError(err?.response?.data?.message || "Could not add that document.");
    } finally {
      setSaving(false);
    }
  };

  const grouped = BUCKETS.map((bucket) => ({
    ...bucket,
    items: documents.filter((doc) => doc.bucket === bucket.value),
  }));

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <Surface sx={{ p: { xs: 2, md: 3 } }}>
        <SectionTitle
          action={
            <Button
              size="small"
              variant="contained"
              startIcon={<MdAdd />}
              onClick={() => setAdding(true)}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                bgcolor: T.primary,
                "&:hover": { bgcolor: "#013838" },
              }}
            >
              Add document
            </Button>
          }
        >
          Documents & photos
        </SectionTitle>

        {loading ? (
          <Typography sx={{ color: T.muted, fontSize: "0.85rem" }}>Loading…</Typography>
        ) : !documents.length ? (
          <Box
            sx={{
              py: 5,
              textAlign: "center",
              borderRadius: 2,
              bgcolor: tint(T.muted, 0.05),
            }}
          >
            <Typography sx={{ color: T.muted, fontSize: "0.88rem" }}>
              No documents on file for this pharmacy.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {grouped
              .filter((group) => group.items.length)
              .map((group) => (
                <Box key={group.value}>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: T.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      mb: 1,
                    }}
                  >
                    {group.label} · {group.items.length}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {group.items.map((doc) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                        <DocumentCard
                          doc={doc}
                          onOpen={() => window.open(doc.url, "_blank", "noopener")}
                          onRemove={() => setRemoving(doc)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
          </Stack>
        )}
      </Surface>

      {/* ── Add ────────────────────────────────────────────────────────── */}
      <Dialog
        open={adding}
        onClose={saving ? undefined : () => setAdding(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add a document</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {addError && <Alert severity="error">{addError}</Alert>}
            <TextField
              select
              size="small"
              label="Type"
              value={form.bucket}
              onChange={(e) => setForm((f) => ({ ...f, bucket: e.target.value }))}
            >
              {BUCKETS.map((bucket) => (
                <MenuItem key={bucket.value} value={bucket.value}>
                  {bucket.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="File URL"
              placeholder="https://…"
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              helperText="Paste the uploaded file's link."
            />
            <TextField
              size="small"
              label="Label (optional)"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setAdding(false)}
            disabled={saving}
            sx={{ textTransform: "none", color: T.muted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={add}
            disabled={saving || !form.url}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              bgcolor: T.primary,
              "&:hover": { bgcolor: "#013838" },
            }}
          >
            {saving ? "Adding…" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(removing)}
        title="Remove this document?"
        description={`${removing?.name || removing?.url} will no longer be linked to this pharmacy. The file itself is not deleted from storage.`}
        confirmLabel="Remove"
        destructive
        requireReason
        onConfirm={async () => {
          await api.removeDocument(pharmacyId, removing.id);
          notify?.("Document removed.");
          await load();
        }}
        onClose={() => setRemoving(null)}
      />
    </Stack>
  );
}
